'use client'

import { useMemo } from 'react'
import Link from 'next/link'
import SectionHeader from '@/components/ui/SectionHeader'
import ThemeIcon from '@/components/ui/ThemeIcon'
import LinkWithIcon from '@/components/ui/LinkWithIcon'
import { SectionWrapper } from '@/components/SectionWrapper'
import { cn } from '@/lib/utils'
import { getBackgroundFromShade, normalizeSectionSettings } from '@/utilities/sectionSettings'
import type { ProductsOverviewBlock, Product } from '@/payload-types'

interface ProductsOverviewClientProps {
  tag?: string
  title?: string
  subtitle?: string
  products: Product[]
  theme?: 'dark' | 'light' | 'white' | 'light-gray'
  moreLink?: ProductsOverviewBlock['moreLink']
  settings?: ProductsOverviewBlock['settings']
}

export default function ProductsOverviewClient({
  tag = 'PRODUKTE ÜBERSICHT',
  title = 'Was wir bieten',
  subtitle = 'Von der Beratung bis zur Umsetzung - wir bieten Ihnen ein umfassendes Leistungsspektrum im Bereich Metallbau und Verglasung.',
  products,
  theme = 'white',
  moreLink,
  settings,
}: ProductsOverviewClientProps) {
  const normalizedSettings = normalizeSectionSettings(settings)
  // Use settings.isDarkMode first, fall back to theme === 'dark'
  const isDarkMode = normalizedSettings?.isDarkMode ?? theme === 'dark'
  const isDark = isDarkMode

  // Compute href for each product and split into columns (memoized for performance)
  const { leftColumnProducts, rightColumnProducts } = useMemo(() => {
    const productsWithHref = products.map((product) => {
      const href =
        product.type === 'internal'
          ? `/produkte/${product.slug}`
          : product.type === 'external' && product.externalUrl
            ? product.externalUrl
            : '#'
      return { ...product, href }
    })

    return {
      leftColumnProducts: productsWithHref.slice(0, Math.ceil(productsWithHref.length / 2)),
      rightColumnProducts: productsWithHref.slice(Math.ceil(productsWithHref.length / 2)),
    }
  }, [products])

  // Determine background - use settings first, then theme
  const bgClass = normalizedSettings?.backgroundShade
    ? getBackgroundFromShade(normalizedSettings.backgroundShade)
    : isDarkMode
      ? 'dark'
      : theme === 'light-gray'
        ? 'light-gray'
        : 'white'
  const titleColorClass = isDark ? 'text-text-white' : 'text-text-primary'
  const subtitleColorClass = isDark ? 'text-text-white/80' : 'text-text-secondary'
  const linkTextClass = isDark ? 'text-text-white' : 'text-text-primary'
  const hoverBgClass = isDark ? 'hover:bg-white/10' : 'hover:bg-black/5'
  const hoverTextClass = isDark ? 'group-hover:text-brand' : 'group-hover:text-brand'
  const borderClass = isDark ? 'border-text-white/20' : 'border-text-primary/20'

  const productLinkClass = cn(
    'block relative border-b',
    borderClass,
    'py-2 px-2.5',
    hoverBgClass,
    'transition-colors duration-200 group',
  )

  const productNameClass = cn(
    'text-lg py-2',
    linkTextClass,
    'leading-none',
    hoverTextClass,
    'transition-colors duration-200',
  )

  return (
    <SectionWrapper
      settings={normalizedSettings}
      background={bgClass}
      isDarkMode={isDarkMode}
    >
      <div className="flex flex-col lg:flex-row gap-12 items-center">
        {/* Icon Section */}
        <div className="w-full lg:w-1/3 flex justify-center lg:justify-start">
          <div className="relative w-80 h-80 lg:w-96 lg:h-96">
            <ThemeIcon
              size={320}
              alt="Lüchinger Metallbau Icon"
              className="w-full h-full object-contain"
              ariaHidden={false}
            />
          </div>
        </div>

        {/* Content Section */}
        <div className="w-full lg:w-2/3 flex flex-col lg:gap-8">
          {/* Header */}
          <SectionHeader
            infoTagText={tag}
            title={title}
            subtitle={subtitle}
            titleClassName={titleColorClass}
            subtitleClassName={subtitleColorClass}
            infoTagAs="h2"
            titleAs="h2"
          />

          {/* Products Grid */}
          {products.length > 0 && (
            <div className="flex flex-col lg:flex-row lg:gap-4">
              {/* Left Column */}
              <div className="flex-1">
                {leftColumnProducts.map((product, index) => (
                  <Link
                    key={product.id}
                    href={product.href}
                    className={cn(productLinkClass, index === 0 && 'border-t')}
                    data-card-type="product"
                    data-card-slug={product.slug || undefined}
                    data-card-block="productsOverview"
                  >
                    <div className={productNameClass}>{product.title}</div>
                  </Link>
                ))}
              </div>

              {/* Right Column */}
              {rightColumnProducts.length > 0 && (
                <div className="flex-1">
                  {rightColumnProducts.map((product, index) => (
                    <Link
                      key={product.id}
                      href={product.href}
                      className={cn(productLinkClass, index === 0 && 'lg:border-t')}
                      data-card-type="product"
                      data-card-slug={product.slug || undefined}
                      data-card-block="productsOverview"
                    >
                      <div className={productNameClass}>{product.title}</div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* More Link */}
          {moreLink && (
            <div className="flex items-center gap-1 mt-6">
              <LinkWithIcon
                link={moreLink}
                variant="link"
                color={isDark ? 'white' : 'brand'}
                size="md"
              />
            </div>
          )}
        </div>
      </div>
    </SectionWrapper>
  )
}
